import { HTMLAttributes, useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Button from '@/components/base/button'
import Container from '@/components/base/container'
import Motion from '@/components/base/motion'
import SVGIcon from '@/components/base/svg-icon'
import TextEffect from '@/components/base/text-effect'
import Typography from '@/components/base/typography'
import { stripSlash } from '@/utils/path'

import { buttonsGroupRevealAnimation } from './animations'
import { cn } from '@/lib/utils'
import { WPLink } from '@/types'

export type ButtonsGroupProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    items: {
      link: WPLink
    }[]
  }>

const ButtonsGroup = ({ items, ...props }: ButtonsGroupProps) => {
  const { asPath } = useRouter()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const isActive = useCallback(
    (url?: string) => !!url && stripSlash(url) === stripSlash(asPath),
    [asPath]
  )

  const handleMobileButtonClick = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const validItems = items?.filter((item) => item.link?.url)

  const activeItem = validItems?.find((item) => isActive(item.link.url))

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <section {...props}>
      <Container>
        {/* Desktop view */}
        <Motion animation={buttonsGroupRevealAnimation}>
          {(motionRef) => (
            <div
              ref={motionRef}
              className="hidden flex-wrap items-center gap-spacing-2xs md:flex"
            >
              {validItems?.map((item, index) => (
                <Button
                  key={index}
                  isLiquid
                  asChild
                  variant={isActive(item.link.url) ? 'accent' : 'secondary'}
                  isActive={isActive(item.link.url)}
                  className="[&_svg[data-background]]:stroke-border-menu-light"
                  data-motion-button
                >
                  <Link
                    href={item.link.url}
                    scroll={false}
                    target={item.link.target || '_self'}
                  >
                    <TextEffect>
                      <Typography variant="regular">
                        {item.link.title}
                      </Typography>
                    </TextEffect>
                  </Link>
                </Button>
              ))}
            </div>
          )}
        </Motion>
        {/* Mobile view with dropdown */}
        <div ref={dropdownRef} className="relative z-20 md:hidden">
          <Button
            isLiquid
            full
            variant="accent"
            className="dark min-h-[4.8rem] w-full justify-between"
            onClick={handleMobileButtonClick}
            endIcon={
              <SVGIcon
                symbol="chevron-down-icon"
                className={cn([
                  'size-[1rem] transform fill-white-light transition-transform',
                  { 'rotate-180': isDropdownOpen }
                ])}
              />
            }
          >
            <Typography variant="regular">
              {activeItem?.link.title || validItems?.[0]?.link.title || ''}
            </Typography>
          </Button>

          {isDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-radius-minimal bg-surface-primay-invert-light shadow-lg">
              <div>
                {validItems?.map((item, index) => (
                  <Link
                    key={index}
                    href={item.link.url}
                    className={cn([
                      'block w-full px-4 py-2 text-left',
                      {
                        'bg-text-primary-dark': isActive(item.link.url)
                      }
                    ])}
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <Typography variant="regular">{item.link.title}</Typography>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </Container>
    </section>
  )
}

export default ButtonsGroup
